
import React from "react";
import { Movie } from "@/types/movie";
import { H1, Span, P } from "@/components/ui/typography";
import MovieTags from "../MovieTags";
import { UnifiedActionButton } from "../buttons";
import { useIsMobile } from "@/hooks/use-mobile";
import { useUnifiedActionButtons } from "@/hooks/useUnifiedActionButtons";
import { getButtonContext } from "@/utils/buttonContextUtils";

interface DialogDetailsProps {
  movie: Movie;
  onToggleFavorite: () => void;
  onTogglePlanToWatch: () => void;
  variant?: "dialog" | "page";
}

const DialogDetails: React.FC<DialogDetailsProps> = ({
  movie,
  onToggleFavorite,
  onTogglePlanToWatch,
  variant = "dialog"
}) => {
  const isMobile = useIsMobile();
  
  const buttonContext = getButtonContext(
    undefined,
    variant === "page" ? "detail" : "dialog",
    false,
    variant === "dialog"
  );
  
  // FIXED: Wrap handlers with proper event prevention
  const wrappedToggleFavorite = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onToggleFavorite();
  };

  const wrappedTogglePlanToWatch = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onTogglePlanToWatch();
  };
  
  const { getFavoriteButtonProps, getWatchlistButtonProps } = useUnifiedActionButtons({
    movie,
    context: buttonContext,
    onToggleFavorite: wrappedToggleFavorite,
    onTogglePlanToWatch: wrappedTogglePlanToWatch
  });
  
  const favoriteProps = getFavoriteButtonProps();
  const watchlistProps = getWatchlistButtonProps();
  
  // Destructure all properties we need from the movie object
  const { title, year, director, overview } = movie;
  const movieTags = Array.isArray(movie.tags) ? movie.tags : [];
  
  // Determine rounding classes based on variant and screen size
  const getRoundingClasses = () => {
    if (variant === "page") {
      return isMobile ? "rounded-b-lg" : "md:rounded-r-lg";
    }
    return "";
  };
  
  return (
    <div className={`
      relative flex flex-col bg-mono-platinum
      ${isMobile 
        ? "flex-1 p-4" 
        : "md:w-1/2 p-6 md:p-8"
      }
      ${getRoundingClasses()}
    `}>
      
      <H1 className={`
        text-mono-eerieBlack mb-2
        ${isMobile ? "text-xl" : ""}
      `}>
        {title}
      </H1>
      
      <div className={`
        flex items-center mb-1
        ${isMobile ? "flex-wrap gap-1" : ""}
      `}>
        <Span className="text-mono-onyx/80 mr-2">{year}</Span>
        <Span className="text-mono-onyx/60 mx-1">•</Span>
        <Span className="text-mono-onyx/80 ml-2">{director}</Span>
      </div>
      
      <div className="mb-6">
        <MovieTags tags={movieTags} className="gap-1.5" maxTags={2} />
      </div>
      
      {overview && (
        <div className="mb-4">
          <P className="text-mono-eerieBlack font-medium mb-1">Overview</P>
          <P className={`
            text-mono-eerieBlack/80
            ${isMobile ? "text-sm" : "text-sm"}
          `}>
            {overview}
          </P>
        </div>
      )}
      
      <div className={`
        mt-auto flex gap-3
        ${isMobile ? "flex-col" : "flex-wrap"}
      `}>
        <UnifiedActionButton
          intent="favorite"
          context={buttonContext}
          isActive={favoriteProps.isActive}
          isDeselected={favoriteProps.isDeselected}
          isHovered={favoriteProps.isHovered}
          onClick={favoriteProps.onClick}
          onMouseEnter={favoriteProps.onMouseEnter}
          onMouseLeave={favoriteProps.onMouseLeave}
          showLabel={true}
        />
        
        <UnifiedActionButton
          intent="watchlist"
          context={buttonContext}
          isActive={watchlistProps.isActive}
          isDeselected={watchlistProps.isDeselected}
          isHovered={watchlistProps.isHovered}
          onClick={watchlistProps.onClick}
          onMouseEnter={watchlistProps.onMouseEnter}
          onMouseLeave={watchlistProps.onMouseLeave}
          showLabel={true}
        />
      </div>
    </div>
  );
};

export default DialogDetails;
